<template>
  <div class="page-container collaborators-page">
    <portal to="page-name">Colaboradores</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Colaboradores</h1>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column form-group">
            <div class="input-group">
              <input
                type="text"
                class="form-input"
                v-model="filterSearch"
                @keydown.enter="find"
                :disabled="deleting"
                placeholder="Pesquisar por nome do colaborador"
                maxlength="100"
                v-focus>
              <button class="btn btn-neutral btn-action input-group-btn btn-icon"
                      :disabled="deleting"
                      @click="find"
                      tabindex="-1">
                <fa-icon :icon="['fal', 'search']" />
              </button>
            </div>
          </div>
          <div class="column col-auto">
            <button class="btn btn-primary btn-icon btn-icon-left mr-2"
                    :disabled="deleting"
                    @click="create">
              <fa-icon :icon="['fal', 'plus']" />Novo colaborador
            </button>
            <dx-dropdown
              class="btn btn-icon btn-action btn-neutral drop-menu"
              :class="{loading: printing}"
              :items="[
                { key: 'print', name: 'Imprimir em PDF' },
                { key: 'sheet', name: 'Gerar planilha' },
              ]"
              :disabled="printing || deleting"
              @select="print"
              label="name"
              direction="right"
            >
              <fa-icon :icon="['fal', 'print']" v-if="!printing" />
            </dx-dropdown>
          </div>
        </div>
        <div class="loading loading-lg mt-2" v-if="loading"></div>
        <template v-else>
          <template v-if="data.items.length === 0">
            <div class="empty mt-2">
              <div class="empty-icon">
                <fa-icon :icon="['fal', 'info-circle']" size="3x" />
              </div>
              <p class="empty-title h5">Colaboradores</p>
              <p class="empty-subtitle">
                Nenhum colaborador encontrado. Verifique os filtros para realizar novas buscas
              </p>
            </div>
          </template>
          <template v-else>
            <table class="table table-striped table-hover">
              <thead>
              <tr>
                <th class="hide-sm" style="width: 40px">#</th>
                <th>Nome</th>
                <th class="hide-sm hide-md">CPF</th>
                <th>Telefone</th>
                <th style="width: 70px" />
              </tr>
              </thead>
              <tbody>
              <tr v-for="(item, i) in data.items" :key="i">
                <td class="hide-sm">{{ i + 1 }}</td>
                <td>
                  <span class="hover-link" v-if="deleting">{{ item.name }}</span>
                  <router-link :to="editLink(item)" v-else>
                    {{ item.name }}
                  </router-link>
                </td>
                <td class="hide-sm hide-md">
                  {{ item.identity ? item.identity.value : '' | cpf }}
                </td>
                <td class="hide-sm hide-md">
                  {{ item.cellphone || item.phone | phone }}
                </td>
                <td class="text-right">
                  <button class="btn btn-sm btn-action btn-icon btn-secondary tooltip mr-1"
                          :disabled="deleting"
                          @click="edit(item)" data-tooltip="Editar">
                    <fa-icon :icon="['fal', 'pencil']"/>
                  </button>
                  <button class="btn btn-sm btn-action btn-icon btn-error tooltip"
                          :class="{loading: deleting && idx === i}"
                          :disabled="deleting"
                          @click="remove(item)" data-tooltip="Excluir">
                    <fa-icon :icon="['fal', 'times']"/>
                  </button>
                </td>
              </tr>
              </tbody>
            </table>
          </template>
        </template>
      </div>
      <div class="card-footer text-center" v-if="data.items.length > 0">
        <button
          v-if="data.hasMore && !loading"
          class="btn btn-primary btn-fixed-width"
          @click="loadMore"
          :disabled="loadingMore"
          :class="{ loading: loadingMore }"
        >Carregar mais</button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  DELETE_COLLABORATOR,
  PRINT_COLLABORATOR_LIST,
} from '@/data/actions/modules/general';

export default {
  data() {
    return {
      idx: null,
      loading: false,
      loadingMore: false,
      printing: false,
      deleting: false,
      filterSearch: '',
      data: {
        items: [],
        hasMore: false,
        limit: 100,
        offset: 0,
      },
    };
  },
  computed: {
    canAccessRemoveCollaborator() {
      if (DELETE_COLLABORATOR) {
        return this.$can(DELETE_COLLABORATOR);
      }
      return true;
    },
    canAccessPrintCollaborator() {
      if (PRINT_COLLABORATOR_LIST) {
        return this.$can(PRINT_COLLABORATOR_LIST);
      }
      return true;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    find() {
      this.data.offset = 0;
      this.load();
    },
    loadMore() {
      this.data.offset += this.data.limit;
      this.load();
    },
    load() {
      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
        active: true,
      };

      if (this.filterSearch.trim().length > 0) {
        params.search = this.filterSearch;
      }

      this.$http.get('/collaborators', { params })
        .then(({ data }) => {
          this.data.hasMore = data.hasMore;
          this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
        })
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao carregar os dados!');
        })
        .finally(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    create() {
      this.$router.push('/collaborators/create');
    },
    edit(item) {
      if (this.deleting) {
        return;
      }
      this.$router.push(`/collaborators/${item.id}/edit`);
    },
    editLink(item) {
      return `/collaborators/${item.id}/edit`;
    },
    remove(item) {
      if (!this.canAccessRemoveCollaborator) {
        this.$toast.error('Acesso não autorizado!');
        return;
      }

      if (this.deleting) {
        return;
      }

      this.$dialog.show('', {
        size: 'md',
        html: `<div class="text-center">
                <p class="text-center h4">Atenção!</p>
                <p>Ao confirmar a remoção,
                 <strong class="text-primary">todos os dados</strong>
                 personalizados serão excluídos.</p>
                <p>Este registro também
                 <strong class="text-primary">será desvinculado</strong>
                 de todas as permissões e demais telas do sistema.</p>
                <div class="h6 text-error text-bold">
                 Essa ação é irreversível. Deseja continuar?
                </div>
              </div>`,
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: (close) => {
              this.deleting = true;
              const idx = this.data.items.indexOf(item);
              this.idx = idx;
              close();
              this.$http
                .delete(`/collaborators/${item.id}`)
                .then(() => {
                  this.data.items.splice(idx, 1);
                })
                .catch(() => {
                  this.$toast.error('Ocorreu um erro. Tente novamente!');
                })
                .finally(() => {
                  this.deleting = false;
                });
            },
          },
        ],
      });
    },
    print({ key }) {
      if (!this.canAccessPrintCollaborator) {
        this.$toast.error('Acesso não autorizado!');
        return null;
      }

      if (this.printing) {
        return null;
      }

      this.printing = true;

      const params = {
        printType: key,
      };

      if (key === 'sheet') {
        return this.$file
          .download('/collaborators/print', params, {
            name: 'colaboradores.xlsx',
          })
          .catch(() => {})
          .then(() => {
            this.printing = false;
          });
      }

      return this.$file
        .print('/collaborators/print', params)
        .catch(() => {
          this.$toast.error('Ocorreu um erro ao imprimir!');
        })
        .finally(() => {
          this.printing = false;
        });
    },
  },
};
</script>

<style lang="scss">
@import '@/assets/scss/_variables.scss';
.collaborators-page {
  .btn-fixed-width {
    font-size: $font-size-lg;
    height: 2.2rem;
    margin: $layout-spacing-lg 0;
    width: 11rem;
  }
}
</style>
